import { supabase } from '../config/supabase'

class SupabaseService {

  // Check if admin has a valid session in localStorage
  _hasValidSession() {
    try {
      const storageKey = 'sb-jfghvgkvokfphzrfazsm-auth-token';
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (!data?.access_token) return false;
      const parts = data.access_token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) return false;
      return true;
    } catch { return false; }
  }

  // Read JWT directly from localStorage (bypasses navigator.locks deadlock)
  _getAccessToken() {
    try {
      const raw = localStorage.getItem('sb-jfghvgkvokfphzrfazsm-auth-token');
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data?.access_token) return null;
      const payload = JSON.parse(atob(data.access_token.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) return null;
      return data.access_token;
    } catch { return null; }
  }

  // ============================================
  // Major Subjects
  // ============================================
  async getMajorSubjects() {
    try {
      const { data, error } = await supabase
        .from('major_subjects')
        .select('*')
        .order('name')
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching major subjects:', error)
      throw error
    }
  }

  async manageMajorSubject(subjectData) {
    try {
      const { data, error } = await supabase
        .from('major_subjects')
        .upsert(subjectData, { onConflict: 'id' })
        .select()
      
      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error managing major subject:', error)
      return { success: false, error: error.message }
    }
  }

  async deleteMajorSubject(id) {
    try {
      const { error } = await supabase
        .from('major_subjects')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting major subject:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Minor Subjects
  // ============================================
  async getMinorSubjects() {
    try {
      const { data, error } = await supabase
        .from('minor_subjects')
        .select('*')
        .order('name')
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching minor subjects:', error)
      throw error
    }
  }

  async manageMinorSubject(subjectData) {
    try {
      const { data, error } = await supabase
        .from('minor_subjects')
        .upsert(subjectData, { onConflict: 'id' })
        .select()
      
      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error managing minor subject:', error)
      return { success: false, error: error.message }
    }
  }

  async deleteMinorSubject(id) {
    try {
      const { error } = await supabase
        .from('minor_subjects')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting minor subject:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Teachers
  // ============================================
  async getTeachers() {
    try {
      const { data, error } = await supabase
        .from('teachers')
        .select('*')
        .order('name')
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching teachers:', error)
      throw error
    }
  }

  async manageTeacher(teacherData) {
    try {
      // Check if user is authenticated
      const token = this._getAccessToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // If ID is provided, update by ID. Otherwise, insert new record
      if (teacherData.id) {
        // Update existing record by ID
        const { data, error } = await supabase
          .from('teachers')
          .update({
            name: teacherData.name,
            email: teacherData.email || null,
            subject: teacherData.subject || teacherData.department || null,
            qualification: teacherData.qualification || null,
            photo_url: teacherData.photo_url || teacherData.image_url || null,
            designation: teacherData.designation || null,
            department: teacherData.department || null,
            phone_number: teacherData.phone_number || teacherData.phone || null,
            joining_date: teacherData.joining_date || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', teacherData.id)
          .select()
        
        if (error) {
          // If RLS error, provide helpful message
          if (error.code === '42501' || error.message.includes('permission') || error.message.includes('policy')) {
            throw new Error('Admin access required. Your email must be in the admin list.')
          }
          throw error
        }
        return { success: true, data: data[0] }
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('teachers')
          .insert({
            name: teacherData.name,
            email: teacherData.email || null,
            subject: teacherData.subject || teacherData.department || null,
            qualification: teacherData.qualification || null,
            photo_url: teacherData.photo_url || teacherData.image_url || null,
            designation: teacherData.designation || null,
            department: teacherData.department || null,
            phone_number: teacherData.phone_number || teacherData.phone || null,
            joining_date: teacherData.joining_date || null
          })
          .select()
        
        if (error) {
          // If RLS error, provide helpful message
          if (error.code === '42501' || error.message.includes('permission') || error.message.includes('policy')) {
            throw new Error('Admin access required. Your email must be in the admin list.')
          }
          throw error
        }
        return { success: true, data: data[0] }
      }
    } catch (error) {
      console.error('Error managing teacher:', error)
      return { success: false, error: error.message }
    }
  }

  async deleteTeacher(id) {
    try {
      // Check if user is authenticated
      const token = this._getAccessToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const { error } = await supabase
        .from('teachers')
        .delete()
        .eq('id', id)
      
      if (error) {
        // If RLS error, provide helpful message
        if (error.code === '42501' || error.message.includes('permission') || error.message.includes('policy')) {
          throw new Error('Admin access required. Your email must be in the admin list.')
        }
        throw error
      }
      
      return { success: true }
    } catch (error) {
      console.error('Error deleting teacher:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Gallery Photos
  // ============================================
  async getGalleryPhotos() {
    try {
      const { data, error } = await supabase
        .from('gallery_photos')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching gallery photos:', error)
      throw error
    }
  }

  async manageGalleryPhoto(photoData) {
    try {
      const token = this._getAccessToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      const { data, error } = await supabase
        .from('gallery_photos')
        .upsert(photoData, { onConflict: 'id' })
        .select()

      if (error) {
        if (error.code === '42501' || error.message?.includes('row-level security') || error.message?.includes('policy')) {
          throw new Error('Admin access required. Ensure migration 016 is applied (admin@gdcbeekapur.org.in in admin_emails) and you logged in with username/password.')
        }
        if (error.message?.includes('401') || error.status === 401) {
          throw new Error('Session expired or invalid. Please log out and log in again with username/password.')
        }
        throw error
      }
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error managing gallery photo:', error)
      return { success: false, error: error.message }
    }
  }

  async deleteGalleryPhoto(id) {
    try {
      // Check if user is authenticated
      const token = this._getAccessToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const { error } = await supabase
        .from('gallery_photos')
        .delete()
        .eq('id', id)
      
      if (error) {
        // If RLS error, provide helpful message
        if (error.code === '42501' || error.message.includes('permission') || error.message.includes('policy')) {
          throw new Error('Admin access required. Your email must be in the admin list.')
        }
        throw error
      }
      
      return { success: true }
    } catch (error) {
      console.error('Error deleting gallery photo:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Storage Operations
  // ============================================
  
  // Upload file to storage
  async uploadFile(bucket, path, file, options = {}) {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: options.upsert || false,
          ...options
        })

      if (error) throw error
      return data
    } catch (error) {
      console.error(`Error uploading file to ${bucket}:`, error)
      throw error
    }
  }

  // Get public URL for file
  getPublicUrl(bucket, path) {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)

    return data.publicUrl
  }

  // Get signed URL for private file (temporary access)
  async getSignedUrl(bucket, path, expiresIn = 3600) {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, expiresIn)

      if (error) throw error
      return data.signedUrl
    } catch (error) {
      console.error('Error creating signed URL:', error)
      throw error
    }
  }

  // Delete file from storage
  async deleteFile(bucket, path) {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .remove([path])

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error
    }
  }

  // ============================================
  // Degree Fee Structures
  // ============================================
  async getDegreeFeeStructures() {
    try {
      const { data, error } = await supabase
        .from('degree_fee_structures')
        .select('*')
        .order('degree')
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching degree fee structures:', error)
      throw error
    }
  }

  async manageDegreeFeeStructure(feeData) {
    try {
      // If ID is provided, update by ID. Otherwise, upsert by degree (for new entries)
      if (feeData.id) {
        // Update existing record by ID
        const { data, error } = await supabase
          .from('degree_fee_structures')
          .update({
            degree: feeData.degree,
            base_fee: feeData.base_fee,
            updated_at: new Date().toISOString()
          })
          .eq('id', feeData.id)
          .select()
        
        if (error) throw error
        return { success: true, data: data[0] }
      } else {
        // Insert new record (upsert by degree to prevent duplicates)
        const { data, error } = await supabase
          .from('degree_fee_structures')
          .upsert({
            degree: feeData.degree,
            base_fee: feeData.base_fee
          }, { onConflict: 'degree' })
          .select()
        
        if (error) throw error
        return { success: true, data: data[0] }
      }
    } catch (error) {
      console.error('Error managing degree fee structure:', error)
      return { success: false, error: error.message }
    }
  }

  async deleteDegreeFeeStructure(id) {
    try {
      const { error } = await supabase
        .from('degree_fee_structures')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting degree fee structure:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Caste Gender Discounts
  // ============================================
  async getCasteGenderDiscounts() {
    try {
      // Always fetch fresh data from Supabase (no caching)
      const { data, error } = await supabase
        .from('caste_gender_discounts')
        .select('*')
        .order('caste')
      
      if (error) throw error
      
      // Log fetched data for debugging
      console.log('📊 Fetched caste/gender discounts from Supabase:', data)
      
      return data || []
    } catch (error) {
      console.error('Error fetching caste gender discounts:', error)
      throw error
    }
  }

  async manageCasteGenderDiscount(discountData) {
    try {
      // If ID is provided, update by ID. Otherwise, insert new record
      if (discountData.id) {
        // Update existing record by ID
        const { data, error } = await supabase
          .from('caste_gender_discounts')
          .update({
            caste: discountData.caste,
            gender: discountData.gender,
            deduction_amount: discountData.deduction_amount || 0,
            discount_percentage: discountData.discount_percentage || 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', discountData.id)
          .select()
        
        if (error) throw error
        return { success: true, data: data[0] }
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('caste_gender_discounts')
          .insert({
            caste: discountData.caste,
            gender: discountData.gender,
            deduction_amount: discountData.deduction_amount || 0,
            discount_percentage: discountData.discount_percentage || 0
          })
          .select()
        
        if (error) throw error
        return { success: true, data: data[0] }
      }
    } catch (error) {
      console.error('Error managing caste gender discount:', error)
      return { success: false, error: error.message }
    }
  }

  async deleteCasteGenderDiscount(id) {
    try {
      const { error } = await supabase
        .from('caste_gender_discounts')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting caste gender discount:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Students
  // ============================================
  async getStudents() {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching students:', error)
      throw error
    }
  }

  async updateStudentPaymentStatus(studentId, paymentStatus) {
    try {
      // Check if user is authenticated
      const token = this._getAccessToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const { data, error } = await supabase
        .from('students')
        .update({
          payment_status: paymentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', studentId)
        .select()
      
      if (error) {
        // If RLS error, provide helpful message
        if (error.code === '42501' || error.message.includes('permission') || error.message.includes('policy')) {
          throw new Error('Admin access required. Your email must be in the admin list.')
        }
        throw error
      }
      
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating student payment status:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Payments
  // ============================================
  async getPayments() {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching payments:', error)
      throw error
    }
  }

  // ============================================
  // Notices (Digital Bulletin Board)
  // ============================================
  async getNotices() {
    try {
      const { data, error } = await supabase
        .from('notices')
        .select('*')
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching notices:', error)
      return []
    }
  }

  async getActiveNotices() {
    try {
      const now = new Date().toISOString()
      const { data, error } = await supabase
        .from('notices')
        .select('*')
        .eq('is_active', true)
        .or(`expires_at.is.null,expires_at.gt.${now}`)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching active notices:', error)
      return []
    }
  }

  async manageNotice(noticeData) {
    try {
      const token = this._getAccessToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (noticeData.id) {
        // Update existing notice
        const { data, error } = await supabase
          .from('notices')
          .update({
            title: noticeData.title,
            content: noticeData.content,
            priority: noticeData.priority || 'normal',
            category: noticeData.category || 'general',
            is_active: noticeData.is_active !== undefined ? noticeData.is_active : true,
            is_pinned: noticeData.is_pinned || false,
            expires_at: noticeData.expires_at || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', noticeData.id)
          .select()
        
        if (error) throw error
        return { success: true, data: data[0] }
      } else {
        // Insert new notice
        const { data, error } = await supabase
          .from('notices')
          .insert({
            title: noticeData.title,
            content: noticeData.content,
            priority: noticeData.priority || 'normal',
            category: noticeData.category || 'general',
            is_active: noticeData.is_active !== undefined ? noticeData.is_active : true,
            is_pinned: noticeData.is_pinned || false,
            expires_at: noticeData.expires_at || null,
            created_by: session.user.email
          })
          .select()
        
        if (error) throw error
        return { success: true, data: data[0] }
      }
    } catch (error) {
      console.error('Error managing notice:', error)
      return { success: false, error: error.message }
    }
  }

  async deleteNotice(id) {
    try {
      const token = this._getAccessToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const { error } = await supabase
        .from('notices')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting notice:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Real-time Subscriptions (optional)
  // ============================================
  
  // Subscribe to table changes
  subscribeToTable(table, callback, filter = {}) {
    const channel = supabase
      .channel(`${table}_changes`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
          ...filter
        },
        callback
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }
}

export default new SupabaseService()

